import React, { useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { ActionCard, Combobox, Icon, MiniWeek, Note, Popover } from '@repo/design-system';
import type { AuthoredOpeningHoursDay } from '@repo/frontend-types';
import type { ReadOpeningHoursOutcome } from '@repo/front-end-services';
import { semantic, space, typeRole } from '@repo/design-tokens';

import type { OrganisationTwins } from '../useOrganisationTwins';
import { cloneWeek, copySourceWeek, miniWeekOf, weekdaysAndWeekends } from './hoursModel';
import { weekRefusal } from './weekRules';
import { usePopoverProblem } from './usePopoverProblem';
import {
  actionProblem,
  HOURS_CHANGED_ELSEWHERE,
  type HoursActionResult,
} from './useOpeningHoursEditor';

/**
 * *Copy another twin* (spec twin-details-page-hours-start, R4): the fourth quick start, and the
 * popover it opens — a `Combobox` of the organisation's other twins. Picking one reads that twin's
 * hours and copies its current standard week straight to this twin (Maria, 2026-10-19): the week
 * covering its today, else its next upcoming one (`copySourceWeek`). Only the days are copied — not
 * its dates, its end, or any special period — and nothing is ever written to the source twin. No
 * frame draws the popover; built on the stated default, for Rob.
 */

/** What a copy sends: the source's name, a copy of its week's days, and any zone difference. */
export interface CopySource {
  name: string;
  days: AuthoredOpeningHoursDay[];
  /**
   * *Times are copied as they are; {name} is in {zone}.* when the source keeps its hours on another
   * clock (R4.4.5), else null — the one place a zone difference could otherwise pass silently.
   */
  zoneNote: string | null;
}

export interface CopyFromTwinProps {
  organisationTwins: OrganisationTwins;
  /** This twin's timezone, or null when its read carried none. */
  hereZone: string | null;
  onRetryTwins: () => void;
  /** Another twin's hours — the editor's `readTwinHours`. */
  readTwinHours: (bid: string, signal?: AbortSignal) => Promise<ReadOpeningHoursOutcome>;
  /** Apply the copied week to this twin — the step's `startWith`, which announces and moves focus. */
  onCopy: (source: CopySource) => Promise<HoursActionResult>;
  /** An outcome that arrived after the popover was closed, for the step to show and say. */
  onLateProblem: (sentence: string) => void;
  className?: string;
}

/** R3.4's sentence for an unknown outcome, shown once the re-read has confirmed it did not land. */
export const START_NOT_SAVED =
  'The opening hours could not be saved. Nothing was changed; try again.';
/** An unknown outcome whose re-read failed too: whether it landed is not known (as `NEW_HOURS_UNKNOWN`). */
export const START_UNCONFIRMED = 'Couldn’t tell whether the opening hours were saved.';

/** What a quick start or a copy says when it did not land: null when it did. */
export const startProblem = (result: HoursActionResult): string | null =>
  result.status === 'failed' && result.unconfirmed
    ? START_UNCONFIRMED
    : actionProblem(result, HOURS_CHANGED_ELSEWHERE, START_NOT_SAVED);

/** The card's sentence (R4.2): who it would copy from, as far as the list says. */
export const copyDescription = (state: OrganisationTwins): string => {
  if (state.status !== 'ready') return 'Start from another twin in this organisation.';
  const [a, b] = state.twins;
  if (!a) return `No other twins in ${state.orgName} yet.`;
  if (!b) return `Start from ${a.name}.`;
  if (state.twins.length === 2) return `Start from ${a.name} or ${b.name}.`;
  return `Start from ${a.name}, ${b.name} or another ${state.orgName} twin.`;
};

/** The copy card's picture: illustrative, since no twin's hours are read before a pick (R4.7). */
const illustrativeWeek = () => miniWeekOf(weekdaysAndWeekends());

/** Whether a source keeps its hours on another clock than this twin's. Unknown on either side is not a difference. */
const zonesDiffer = (here: string | null, there: string | null): boolean =>
  here !== null && there !== null && here !== there;

const CopyFromTwin: React.FC<CopyFromTwinProps> = ({
  organisationTwins,
  hereZone,
  onRetryTwins,
  readTwinHours,
  onCopy,
  onLateProblem,
  className,
}) => {
  const cardRef = useRef<HTMLButtonElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();
  // The read after a refused copy can take this popover away with the start panel (someone else
  // added hours). A write's outcome it shows goes to the step, as `NewStandardHours` does; a source
  // read's problem does not — it is about an attempt nothing was written for (`forward: false`).
  const popover = usePopoverProblem(onLateProblem);
  const { open, problem: error, report } = popover;
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState('');
  /** The pick in flight: a newer pick, a close or an unmount aborts it and drops its answer. */
  const pickRef = useRef<AbortController | null>(null);
  /** A copy write is in flight: it cannot be taken back, so no other pick starts until it settles. */
  const writingRef = useRef(false);

  useEffect(() => () => pickRef.current?.abort(), []);

  const close = () => {
    pickRef.current?.abort();
    pickRef.current = null;
    popover.close();
    setPicked(undefined);
    setStatus('');
  };

  const focusCombobox = () =>
    requestAnimationFrame(() => bodyRef.current?.querySelector<HTMLInputElement>('input')?.focus());

  const ready = organisationTwins.status === 'ready' ? organisationTwins : null;
  const options = (ready?.twins ?? []).map(t => ({ value: t.bid, label: t.name, meta: t.bid }));

  const pick = async (bid: string) => {
    const twin = ready?.twins.find(t => t.bid === bid);
    if (!twin || writingRef.current) return;
    pickRef.current?.abort();
    const controller = new AbortController();
    pickRef.current = controller;
    setPicked(bid);
    report(null);
    setStatus(`Reading ${twin.name}’s hours…`);
    let outcome: ReadOpeningHoursOutcome;
    try {
      outcome = await readTwinHours(bid, controller.signal);
    } catch {
      outcome = { status: 'failed' };
    }
    // A newer pick or a close superseded this one: its answer is about nothing on screen.
    if (controller.signal.aborted) return;
    /** A problem shown in the popover; only a write's is the step's if the popover goes. */
    const fail = (sentence: string, forward = false) => {
      setStatus('');
      report(sentence, { forward });
      focusCombobox();
    };
    if (outcome.status !== 'ready') {
      // eslint-disable-next-line no-console
      console.error(
        '[hours-start] source hours could not be read:',
        bid,
        outcome.httpStatus ?? 'no status',
      );
      fail(`Couldn’t read ${twin.name}’s opening hours.`);
      return;
    }
    const source = outcome.hours;
    const week = copySourceWeek(source);
    if (!week) {
      fail(`${twin.name} has no standard hours to copy.`);
      return;
    }
    // A week the step's own rules refuse is never sent (R4.6).
    const refusal = weekRefusal(week.days);
    if (refusal) {
      fail(refusal);
      return;
    }
    const thereZone = 'timezone' in source && source.timezone ? source.timezone : null;
    setStatus(`Copying ${twin.name}’s week…`);
    writingRef.current = true;
    let result: HoursActionResult;
    try {
      result = await onCopy({
        name: twin.name,
        days: cloneWeek(week.days),
        zoneNote: zonesDiffer(hereZone, thereZone)
          ? `Times are copied as they are; ${twin.name} is in ${thereZone}.`
          : null,
      });
    } finally {
      writingRef.current = false;
    }
    if (pickRef.current === controller) pickRef.current = null;
    const problem = startProblem(result);
    if (problem === null) {
      // The step moves focus to the new week's title; the popover goes with the start panel.
      popover.close();
      return;
    }
    if (popover.isOpen()) fail(problem, true);
    else report(problem);
  };

  const disabled = ready !== null && ready.twins.length === 0;

  return (
    <>
      <ActionCard
        ref={cardRef}
        className={className}
        icon="copy"
        tone="peri"
        title="Copy another twin"
        description={copyDescription(organisationTwins)}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={() => {
          if (!open) popover.show();
        }}
      >
        <MiniWeek days={illustrativeWeek()} />
      </ActionCard>
      <Popover
        id={popoverId}
        open={open}
        onClose={close}
        anchorRef={cardRef}
        title="Copy another twin"
        glyph="copy"
        placement="bottom"
        width={360}
      >
        <Body ref={bodyRef}>
          <Combobox
            aria-label="Twin to copy from"
            options={options}
            value={picked}
            onChange={value => {
              if (value) void pick(value);
            }}
            loading={organisationTwins.status === 'pending'}
            loadingText="Reading this organisation’s twins…"
            error={
              organisationTwins.status === 'failed'
                ? 'Couldn’t read this organisation’s twins.'
                : undefined
            }
            onRetry={onRetryTwins}
            retryLabel="Try again"
            emptyText="No twin matches."
            // Every other twin of one organisation — `useOrganisationTwins`' list, bounded by the
            // organisation (11 at most on staging) — so it scrolls rather than hiding any behind a cap.
            maxResults={Infinity}
            commitOnClose={false}
            onGlass
          />
          {/* Mounted empty, so what it is given next is heard (Block A.4). */}
          <div role="status" className="status">
            {status}
          </div>
          <div role="status">
            {error && (
              <Note tone="error" icon={<Icon name="circle-exclamation" size="xs" />}>
                {error}
              </Note>
            )}
          </div>
        </Body>
      </Popover>
    </>
  );
};

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};

  .status:not(:empty) {
    margin: 0;
    ${typeRole.caption1};
    color: ${semantic.text.secondary};
  }
`;

export default CopyFromTwin;
